/**
 * Schema.org structured data generator for ApartmentComplex
 * Follows official schema.org/ApartmentComplex specification
 * Enhances SEO, AIO (AI-powered search), and AEO (Answer Engine Optimization)
 */

/**
 * Gets GPS coordinates from Contentful exactLocation field or falls back to approximate coordinates
 */
const getCoordinates = (exactLocation, citySlug, area) => {
  // Use exact coordinates from Contentful if available
  if (exactLocation && exactLocation.lat && exactLocation.lon) {
    return { lat: exactLocation.lat, lng: exactLocation.lon };
  }
  
  // Fallback to approximate coordinates based on city
  const michiganCoordinates = {
    'ann-arbor': { lat: 42.2808, lng: -83.7430 },
    'ypsilanti': { lat: 42.2411, lng: -83.6130 },
    'detroit': { lat: 42.3314, lng: -83.0458 },
    'lansing': { lat: 42.7325, lng: -84.5555 },
    'grand-rapids': { lat: 42.9634, lng: -85.6681 }
  };

  const floridaCoordinates = {
    'orlando': { lat: 28.5383, lng: -81.3792 },
    'winter-park': { lat: 28.6000, lng: -81.3392 },
    'tampa': { lat: 27.9506, lng: -82.4572 },
    'jacksonville': { lat: 30.3322, lng: -81.6557 },
    'miami': { lat: 25.7617, lng: -80.1918 }
  };

  // Return coordinates based on state
  if (area === 'MI' || area === 'Michigan' || area === 'mi') {
    return michiganCoordinates[citySlug] || { lat: 42.2808, lng: -83.7430 }; // Default to Ann Arbor
  } else {
    return floridaCoordinates[citySlug] || { lat: 28.5383, lng: -81.3792 }; // Default to Orlando
  }
};

/**
 * Formats phone number to international format
 */
const formatPhoneNumber = (phone) => {
  if (!phone) return null;
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  // Add US country code if not present
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
};

/**
 * Parses office hours from Contentful format into structured schema.org format
 */
const parseOfficeHours = (hoursData) => {
  const defaultHours = [
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "09:00", "closes": "18:00" },
    { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "10:00", "closes": "17:00" },
    { "@type": "OpeningHoursSpecification", "dayOfWeek": "Sunday", "opens": "12:00", "closes": "17:00" }
  ];

  if (!hoursData) return defaultHours;

  try {
    // Parse Contentful hours format (e.g., "Mon 9:00am - 6:00pm\nTue 9:00am - 6:00pm...")
    const hoursArray = typeof hoursData === 'string' 
      ? hoursData.split('\n').map(line => line.trim()).filter(Boolean)
      : Array.isArray(hoursData) ? hoursData : [];

    if (hoursArray.length === 0) return defaultHours;

    const dayMap = {
      'Mon': 'Monday',
      'Tue': 'Tuesday',
      'Wed': 'Wednesday',
      'Thu': 'Thursday',
      'Fri': 'Friday',
      'Sat': 'Saturday',
      'Sun': 'Sunday'
    };

    const parsedHours = [];
    const groupedHours = {}; // Group days with same hours

    hoursArray.forEach(line => {
      const [dayPart, ...rest] = line.split(' ');
      const day = dayPart.trim();
      const times = rest.join(' ').trim();
      
      if (dayMap[day] && times && times !== 'Closed' && times.includes(' - ')) {
        const timeParts = times.split(' - ');
        if (timeParts.length === 2) {
          const [startStr, endStr] = timeParts;
          const opens = convertTo24Hour(startStr.trim());
          const closes = convertTo24Hour(endStr.trim());
          
          if (opens && closes) {
            const key = `${opens}-${closes}`;
            if (!groupedHours[key]) {
              groupedHours[key] = [];
            }
            groupedHours[key].push(dayMap[day]);
          }
        }
      }
    });

    // Convert grouped hours to schema format
    Object.entries(groupedHours).forEach(([timeRange, days]) => {
      const [opens, closes] = timeRange.split('-');
      parsedHours.push({
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": days,
        "opens": opens,
        "closes": closes
      });
    });

    return parsedHours.length > 0 ? parsedHours : defaultHours;
  } catch (error) {
    console.error('Error parsing office hours:', error);
    return defaultHours;
  }
};

/**
 * Converts 12-hour time format to 24-hour format for schema.org
 */
const convertTo24Hour = (timeString) => {
  if (!timeString) return null;
  
  const regex = /(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i;
  const match = timeString.match(regex);
  
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2] || '00';
    const period = match[3].toUpperCase();
    
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  }
  
  return null;
};

/**
 * Calculates total and available units from floorplans
 */
const calculateUnitCounts = (floorplans) => {
  if (!floorplans || !Array.isArray(floorplans)) {
    return { total: undefined, available: undefined };
  }
  
  let totalUnits = 0;
  let availableUnits = 0;
  
  floorplans.forEach(({ node }) => {
    // Add actual unit counts if available in the data
    // This is an estimate based on typical apartment complex sizes
    totalUnits += 10; // Placeholder - should be replaced with actual data
    availableUnits += node.available ? 2 : 0; // Placeholder
  });
  
  return { 
    total: totalUnits > 0 ? totalUnits : undefined,
    available: availableUnits > 0 ? availableUnits : undefined
  };
};

/**
 * Extracts bedroom range from floorplans
 */
const getBedroomRange = (floorplans, maxBeds) => {
  if (!floorplans || !Array.isArray(floorplans)) {
    return maxBeds ? { min: 0, max: parseInt(maxBeds) } : undefined;
  }
  
  const bedCounts = floorplans.map(({ node }) => node.beds || 0);
  const minBeds = Math.min(...bedCounts);
  const maxBedsCalculated = Math.max(...bedCounts);
  
  return {
    min: minBeds,
    max: maxBedsCalculated
  };
};

/**
 * Generates ApartmentComplex schema following schema.org specification
 */
export const generateApartmentComplexSchema = (communityData, floorplans) => {
  const {
    communityName,
    address,
    phone,
    email,
    slug,
    citySlug,
    area,
    description,
    seoDescription,
    hours,
    hoursData,
    exactLocation,
    managerName,
    hero,
    heroSlides,
    amenities,
    textOnlyAmenities,
    utilitiesIncluded,
    maxBeds,
    petsAllowed,
    favoriteSpots,
    neighborhood
  } = communityData;

  const coordinates = getCoordinates(exactLocation, citySlug, area);
  const formattedPhone = formatPhoneNumber(phone);
  const officeHours = parseOfficeHours(hoursData || hours?.hours || hours);
  const unitCounts = calculateUnitCounts(floorplans);
  const bedroomRange = getBedroomRange(floorplans, maxBeds);
  
  // Parse address components
  const addressParts = (address && typeof address === 'string') 
    ? address.split(',').map(part => part.trim()) 
    : [];
  const streetAddress = addressParts[0] || '';
  const cityName = addressParts[1] || citySlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const stateAndZip = addressParts[2] || area;
  
  // Extract state and zip code
  let state = area;
  let zipCode = '';
  
  if (stateAndZip) {
    const zipMatch = stateAndZip.match(/(\w+)\s+(\d{5})/);
    if (zipMatch) {
      const stateStr = zipMatch[1];
      if (stateStr.toLowerCase() === 'michigan') {
        state = 'MI';
      } else if (stateStr.toLowerCase() === 'florida') {
        state = 'FL';
      } else {
        state = stateStr.toUpperCase();
      }
      zipCode = zipMatch[2];
    }
  }

  const stateSlug = (area === 'MI' || area === 'Michigan') ? 'michigan' : 'florida';
  const baseUrl = `https://www.mckinley.com/apartments/${stateSlug}/${citySlug}/${slug}`;

  // Build the main ApartmentComplex schema
  const schema = {
    "@context": "https://schema.org",
    "@type": "ApartmentComplex",
    "@id": baseUrl,
    "name": communityName,
    "alternateName": `${communityName} Apartments`,
    "description": seoDescription?.seoDescription || description || `${communityName} offers quality apartment living in ${cityName}, ${state}`,
    "url": baseUrl,
    "tourBookingPage": `${baseUrl}#schedule-tour`,
    "telephone": formattedPhone,
    "email": email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": streetAddress,
      "addressLocality": cityName,
      "addressRegion": state,
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": coordinates.lat,
      "longitude": coordinates.lng
    },
    "openingHoursSpecification": officeHours
  };

  // Add optional zip code
  if (zipCode) {
    schema.address.postalCode = zipCode;
  }

  // Add unit counts if available
  if (unitCounts.total) {
    schema.numberOfAccommodationUnits = {
      "@type": "QuantitativeValue",
      "value": unitCounts.total
    };
  }
  
  if (unitCounts.available) {
    schema.numberOfAvailableAccommodationUnits = {
      "@type": "QuantitativeValue",
      "value": unitCounts.available
    };
  }

  // Add bedroom range
  if (bedroomRange) {
    schema.numberOfBedrooms = {
      "@type": "QuantitativeValue",
      "minValue": bedroomRange.min,
      "maxValue": bedroomRange.max,
      "unitText": "bedrooms"
    };
  }

  // Add pets allowed
  schema.petsAllowed = petsAllowed || "Cats and dogs welcome with restrictions";

  // Add images from hero slides
  const images = [];
  
  // Use heroSlides if available
  if (heroSlides && heroSlides.length > 0) {
    heroSlides.forEach(slide => {
      if (slide?.gatsbyImageData?.images?.fallback?.src) {
        const slideSrc = slide.gatsbyImageData.images.fallback.src;
        images.push(slideSrc.startsWith('http') ? slideSrc : `https://www.mckinley.com${slideSrc}`);
      }
    });
  } else if (hero?.gatsbyImageData?.images?.fallback?.src) {
    // Fallback to single hero image if no slides
    const heroSrc = hero.gatsbyImageData.images.fallback.src;
    images.push(heroSrc.startsWith('http') ? heroSrc : `https://www.mckinley.com${heroSrc}`);
  }
  
  if (images.length > 0) {
    schema.image = images;
  }

  // Merge all amenities together
  const amenityFeatures = [];
  const amenitySet = new Set(); // Use Set to avoid duplicates
  
  // Add structured amenities
  if (amenities && amenities.length > 0) {
    amenities.forEach(amenity => {
      if (amenity.title && !amenitySet.has(amenity.title)) {
        amenitySet.add(amenity.title);
        amenityFeatures.push({
          "@type": "LocationFeatureSpecification",
          "name": amenity.title,
          "value": true
        });
      }
    });
  }

  // Add text-only amenities (handle both string and array formats)
  if (textOnlyAmenities) {
    let textAmenities = [];
    
    // Handle both string (comma-separated) and array formats
    if (typeof textOnlyAmenities === 'string') {
      textAmenities = textOnlyAmenities.split(',').map(a => a.trim()).filter(Boolean);
    } else if (Array.isArray(textOnlyAmenities)) {
      textAmenities = textOnlyAmenities.filter(Boolean);
    }
    
    textAmenities.forEach(amenity => {
      if (!amenitySet.has(amenity)) {
        amenitySet.add(amenity);
        amenityFeatures.push({
          "@type": "LocationFeatureSpecification",
          "name": amenity,
          "value": true
        });
      }
    });
  }

  // Add utilities as amenity features
  if (utilitiesIncluded && typeof utilitiesIncluded === 'string') {
    const utilities = utilitiesIncluded.split(',').map(u => u.trim()).filter(Boolean);
    utilities.forEach(utility => {
      const utilityName = `${utility} Included`;
      if (!amenitySet.has(utilityName)) {
        amenitySet.add(utilityName);
        amenityFeatures.push({
          "@type": "LocationFeatureSpecification",
          "name": utilityName,
          "value": true
        });
      }
    });
  }

  if (amenityFeatures.length > 0) {
    schema.amenityFeature = amenityFeatures;
  }


  // Add contact point for manager
  if (managerName || phone || email) {
    schema.contactPoint = {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "telephone": formattedPhone,
      "email": email,
      "availableLanguage": ["English", "Spanish"]
    };
    
    if (managerName) {
      schema.contactPoint.name = managerName;
    }
  }

  // Add accommodation floor plans if available
  if (floorplans && floorplans.length > 0) {
    schema.accommodationFloorPlan = floorplans.slice(0, 10).map(({ node }) => ({
      "@type": "FloorPlan",
      "name": node.floorplanTitle || node.floorplanName,
      "url": `${baseUrl}/floorplans/${node.floorplanSlug}`,
      "floorSize": node.sqFt ? {
        "@type": "QuantitativeValue",
        "value": node.sqFt,
        "unitCode": "FTK",
        "unitText": "square feet"
      } : undefined
    }));
  }


  // Add additional properties for neighborhood and favorite spots
  if (neighborhood?.neighborhood || favoriteSpots) {
    const additionalProperties = [];
    
    if (neighborhood?.neighborhood) {
      additionalProperties.push({
        "@type": "PropertyValue",
        "name": "Neighborhood Description",
        "value": neighborhood.neighborhood
      });
    }
    
    if (favoriteSpots && favoriteSpots.length > 0) {
      additionalProperties.push({
        "@type": "PropertyValue",
        "name": "Nearby Attractions",
        "value": favoriteSpots.map(spot => spot.title).join(', ')
      });
    }
    
    if (additionalProperties.length > 0) {
      schema.additionalProperty = additionalProperties;
    }
  }

  return schema;
};

/**
 * Generates Offer schema for floor plans
 */
export const generateOfferSchema = (communityData, floorplanData) => {
  const {
    communityName,
    slug,
    citySlug,
    area,
    phone,
    email
  } = communityData;

  const {
    floorplanName,
    floorplanTitle,
    floorplanSlug,
    beds,
    baths,
    numBaths,
    sqFt,
    floorplanImages,
    bannerImage
  } = floorplanData;

  const stateSlug = (area === 'MI' || area === 'Michigan') ? 'michigan' : 'florida';
  const baseUrl = `https://www.mckinley.com/apartments/${stateSlug}/${citySlug}/${slug}`;
  const floorplanUrl = `${baseUrl}/floorplans/${floorplanSlug}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Offer",
    "@id": floorplanUrl,
    "name": floorplanTitle || floorplanName,
    "description": `${beds || 'Studio'} bedroom, ${numBaths || baths} bathroom apartment at ${communityName}. ${sqFt ? `${sqFt} square feet of living space.` : ''}`,
    "url": floorplanUrl,
    "availability": "https://schema.org/InStock",
    "businessFunction": "http://purl.org/goodrelations/v1#LeaseOut",
    "priceSpecification": {
      "@type": "UnitPriceSpecification",
      "priceCurrency": "USD",
      "unitCode": "MON",
      "unitText": "month"
    },
    "offeredBy": {
      "@type": "ApartmentComplex",
      "@id": baseUrl,
      "name": communityName,
      "telephone": formatPhoneNumber(phone),
      "email": email
    }
  };

  // Add images
  const images = [];
  if (bannerImage?.gatsbyImageData?.images?.fallback?.src) {
    const bannerSrc = bannerImage.gatsbyImageData.images.fallback.src;
    images.push(bannerSrc.startsWith('http') ? bannerSrc : `https://www.mckinley.com${bannerSrc}`);
  }
  
  if (floorplanImages && floorplanImages.length > 0) {
    floorplanImages.forEach(img => {
      if (img?.gatsbyImageData?.images?.fallback?.src) {
        const imgSrc = img.gatsbyImageData.images.fallback.src;
        images.push(imgSrc.startsWith('http') ? imgSrc : `https://www.mckinley.com${imgSrc}`);
      }
    });
  }
  
  if (images.length > 0) {
    schema.image = images;
  }

  // Add floor plan details
  schema.itemOffered = {
    "@type": "Accommodation",
    "name": `${beds || 'Studio'} Bedroom Apartment`,
    "numberOfBedrooms": beds || 0,
    "numberOfBathroomsTotal": numBaths || baths,
    "floorSize": sqFt ? {
      "@type": "QuantitativeValue",
      "value": sqFt,
      "unitCode": "FTK",
      "unitText": "square feet"
    } : undefined,
    "petsAllowed": "Cats and dogs welcome with restrictions"
  };

  return schema;
};

/**
 * Generates BreadcrumbList schema for navigation
 */
export const generateBreadcrumbSchema = (communityData) => {
  const { communityName, slug, citySlug, area } = communityData;
  const cityName = citySlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const stateName = (area === 'MI' || area === 'Michigan') ? 'Michigan' : 'Florida';
  const stateSlug = (area === 'MI' || area === 'Michigan') ? 'michigan' : 'florida';

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.mckinley.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Apartments",
        "item": "https://www.mckinley.com/apartments"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": stateName,
        "item": `https://www.mckinley.com/apartments/${stateSlug}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": cityName,
        "item": `https://www.mckinley.com/apartments/${stateSlug}/${citySlug}`
      },
      {
        "@type": "ListItem",
        "position": 5,
        "name": communityName,
        "item": `https://www.mckinley.com/apartments/${stateSlug}/${citySlug}/${slug}`
      }
    ]
  };
};

/**
 * Generates breadcrumb schema for floor plan pages
 */
export const generateFloorplanBreadcrumbSchema = (communityData, floorplanData) => {
  const { communityName, slug, citySlug, area } = communityData;
  const { floorplanName, floorplanSlug } = floorplanData;
  const cityName = citySlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const stateName = (area === 'MI' || area === 'Michigan') ? 'Michigan' : 'Florida';
  const stateSlug = (area === 'MI' || area === 'Michigan') ? 'michigan' : 'florida';

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.mckinley.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Apartments",
        "item": "https://www.mckinley.com/apartments"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": stateName,
        "item": `https://www.mckinley.com/apartments/${stateSlug}`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": cityName,
        "item": `https://www.mckinley.com/apartments/${stateSlug}/${citySlug}`
      },
      {
        "@type": "ListItem",
        "position": 5,
        "name": communityName,
        "item": `https://www.mckinley.com/apartments/${stateSlug}/${citySlug}/${slug}`
      },
      {
        "@type": "ListItem",
        "position": 6,
        "name": "Floor Plans",
        "item": `https://www.mckinley.com/apartments/${stateSlug}/${citySlug}/${slug}/floorplans`
      },
      {
        "@type": "ListItem",
        "position": 7,
        "name": floorplanName,
        "item": `https://www.mckinley.com/apartments/${stateSlug}/${citySlug}/${slug}/floorplans/${floorplanSlug}`
      }
    ]
  };
};

/**
 * Legacy function for backward compatibility - generates LocalBusiness schema
 * @deprecated Use generateApartmentComplexSchema instead
 */
export const generateLocalBusinessSchema = (communityData) => {
  // For backward compatibility, delegate to the new function
  return generateApartmentComplexSchema(communityData, null);
};

/**
 * Combines all schemas for a community page
 */
export const generateAllSchemas = (communityData, floorplans) => {
  const apartmentComplexSchema = generateApartmentComplexSchema(communityData, floorplans);
  const breadcrumbSchema = generateBreadcrumbSchema(communityData);

  // Return as @graph array
  return {
    "@context": "https://schema.org",
    "@graph": [
      apartmentComplexSchema,
      breadcrumbSchema
    ]
  };
};

/**
 * Generates schema for individual floor plan page
 */
export const generateFloorplanSchema = (communityData, floorplanData) => {
  const offerSchema = generateOfferSchema(communityData, floorplanData);
  return offerSchema;
};

/**
 * Combines all schemas for a floor plan page
 */
export const generateFloorplanSchemas = (communityData, floorplanData) => {
  const offerSchema = generateOfferSchema(communityData, floorplanData);
  const breadcrumbSchema = generateFloorplanBreadcrumbSchema(communityData, floorplanData);

  // Return as @graph array
  return {
    "@context": "https://schema.org",
    "@graph": [
      offerSchema,
      breadcrumbSchema
    ]
  };
};